import { progression, ui } from '../core/tunables.js';
import { claimUnlocks, progressToNext, type UnlockDef } from '../core/game/unlock-rules.js';
import { iconElement } from '../content/icons.js';
import { prizeArt } from '../content/prize-art.js';
import { audio } from '../audio/audio.js';
import { missionFor } from './placeholder-content.js';
import { defaultLoadDeco } from './celebration-screen.js';
import { bigButton, el, GLYPH, GLYPH_ICON, timeout } from './dom.js';
import type { Screen } from './router.js';

/**
 * "Good pup." The treat jar fills up and the item she fetched drops into it.
 *
 * No numbers: a three-year-old cannot read a score, and a score invites comparison, which invites
 * losing. Progress is shown as a jar getting fuller - a quantity she can see.
 *
 * Points are awarded when this screen *mounts*, not when its button is pressed. She often wanders off
 * mid-celebration, and wandering off must never cost her the reward.
 */

export interface RewardScreenOptions {
  readonly loadIcon?: (id: string) => Promise<SVGSVGElement | null>;
  /** Loads the prize's own picture for the unlock reveal - a car's outline, or a set's stickers. */
  readonly loadPrizeArt?: (unlock: UnlockDef) => Promise<HTMLElement | null>;
  /** Loads the balloon dressing. Decoration only: a pack without it costs the balloons, never the jar. */
  readonly loadDeco?: () => Promise<SVGSVGElement | null>;
}

export function createRewardScreen(options: RewardScreenOptions = {}): Screen {
  const loadIcon = options.loadIcon ?? iconElement;
  const loadPrizeArt = options.loadPrizeArt ?? prizeArt;
  const loadDeco = options.loadDeco ?? defaultLoadDeco;

  let disposed = false;
  const disposers: (() => void)[] = [];

  return {
    mount(root, ctx) {
      const missionId = ctx.state.missionId;
      const mission = missionFor(missionId);
      /*
       * The mission's own points, plus a little for a bigger load.
       *
       * A four-item run paying the same as a two-item run is a lesson in not bothering. Sublinear on
       * purpose - see `progression.loadBonusPoints` - and clamped to the table's length, because the count
       * comes from the route and a forced state could carry anything.
       */
      const bonus = progression.loadBonusPoints[
        Math.max(0, Math.min(progression.loadBonusPoints.length - 1, ctx.state.itemCount))
      ] ?? 0;
      const earned = (mission?.rewardPoints ?? progression.pointsPerMission) + bonus;

      /*
       * Award first, before any animation - she wanders off mid-celebration constantly and that must
       * never cost her the reward.
       *
       * The totals are computed here rather than read back from `ctx.save`, which is a mount-time
       * snapshot and would still show the pre-award figures. Reading it back is how an earlier version
       * awarded the points and then concluded she could not yet afford a hat.
       */
      const pointsBefore = ctx.save.pawPoints;
      const pointsAfter = missionId === null ? pointsBefore : pointsBefore + earned;

      if (missionId !== null) {
        ctx.updateSave((save) => ({
          ...save,
          pawPoints: save.pawPoints + earned,
          missionHistory: [
            ...save.missionHistory,
            {
              missionId,
              carId: ctx.state.carId ?? '',
              at: new Date().toISOString(),
              pointsAwarded: earned,
            },
          ],
        }));
      }

      /*
       * The jar fills toward the next unlock rather than on an arbitrary cycle, so what she is
       * watching actually means something: full jar, new thing.
       */
      const filledBefore = progressToNext(pointsBefore, ctx.save.unlocks);

      // Hand over anything she has earned. Idempotent, so a remount cannot announce a prize twice.
      const claim = claimUnlocks(pointsAfter, ctx.save.unlocks);
      if (claim.revealed.length > 0) {
        ctx.updateSave((save) => ({ ...save, unlocks: claim.owned }));
      }
      const filledAfter =
        claim.revealed.length > 0 ? 1 : progressToNext(pointsAfter, claim.owned);

      const fill = el('div', { className: 'jar-fill' });
      fill.style.height = `${filledBefore * 100}%`;

      const prize = el('div', { className: 'reward-prize' });
      const reveal = el('div', { className: 'reward-unlock' });

      // The same bunches as the celebration beats, in the same corners: one party, four screens long.
      const balloons = (['left', 'right'] as const).map((side) =>
        el('div', { className: `party-balloons party-balloons--${side}` }),
      );

      const screen = el('div', {
        className: 'screen screen--reward',
        children: [
          // One element per sheet of dots: they fall at different speeds, and a transform can only move a
          // whole element - see the note in the stylesheet.
          el('div', {
            className: 'reward-confetti',
            children: [1, 2, 3].map((layer) =>
              el('div', { className: `reward-confetti-layer reward-confetti-layer--${layer}` }),
            ),
          }),
          ...balloons,
          el('div', {
            className: 'reward-stage',
            children: [
              prize,
              el('div', { className: 'jar', children: [fill, el('div', { className: 'jar-glass' })] }),
              reveal,
            ],
          }),
          el('div', {
            className: 'reward-actions',
            children: [
              bigButton(GLYPH.confirm, () => ctx.dispatch({ type: 'REWARD_DONE' }), {
                variant: 'go',
                icon: GLYPH_ICON.confirm,
              }),
            ],
          }),
        ],
      });
      root.append(screen);

      void loadDeco().then((art) => {
        if (disposed || art === null) return;
        for (const host of balloons) {
          host.append(art.cloneNode(true));
          host.classList.add('is-in');
        }
      });

      // Show what she fetched, then let the jar rise to its new level a beat later, so the two read as
      // cause and effect rather than as one busy moment.
      if (mission !== null) {
        void loadIcon(mission.itemId).then((icon) => {
          if (disposed || icon === null) return;
          prize.replaceChildren(icon);
          prize.classList.add('is-in');
        });
      }

      disposers.push(
        timeout(600, () => {
          fill.style.height = `${filledAfter * 100}%`;
        }),
      );

      /*
       * The unlock lands after the jar has visibly filled, never at the same time. Two things
       * happening at once is one confusing moment; one after the other is cause and effect.
       *
       * The dearest of them when several arrive at once - a restored save can afford a lump - and it shows
       * the prize's own picture, so a car and a set of stickers come through the same door the hats did. A
       * set is three stickers in the one card rather than three reveals: three at a time is what makes a
       * set read as a present.
       */
      const prizeUnlock: UnlockDef | undefined = claim.revealed.at(-1);
      if (prizeUnlock !== undefined) {
        disposers.push(
          timeout(1600, () => {
            audio.fanfare();
            void loadPrizeArt(prizeUnlock).then((art) => {
              if (disposed || art === null) return;
              reveal.replaceChildren(art);
              reveal.classList.add('is-in');
            });
          }),
        );
      }

      // Nothing here needs her to act: if she just watches, the game carries on by itself. Longer
      // when there is a prize to look at.
      const linger = (ui.arriveBeatSeconds + 6 + (prizeUnlock === undefined ? 0 : 4)) * 1000;
      disposers.push(timeout(linger, () => ctx.dispatch({ type: 'REWARD_DONE' })));
    },

    unmount() {
      disposed = true;
      for (const dispose of disposers) dispose();
      disposers.length = 0;
    },
  };
}
